import React, { createContext, useState, useEffect, useRef, useCallback, useContext, useMemo } from "react";
import axios from "axios";
import { nanoid } from 'nanoid';
import { useAuth } from "./AuthContext";
import { HAMPER_DISCOUNT } from "../../src/config/discounts";
const CartContext = createContext();
export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const userEmail = user?.email || null;

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const [selectedAccessories, setSelectedAccessories] = useState([]);
  const [selectedFlowers, setSelectedFlowers] = useState([]);
  const [selectedFlower, setSelectedFlower] = useState(null);
  const [selectedVips, setSelectedVips] = useState([]);
  const [hamperType, setHamperType] = useState(null);
  const [hamperQuantity, setHamperQuantity] = useState(1);
  const [hamperItems, setHamperItems] = useState([]);
  const buildModeRef = useRef(false);
  const buildModeUsedRef = useRef(false);
  
  const [siteSettings, setSiteSettings] = useState(null);
  const [useSavedAddress, setUseSavedAddress] = useState(false);
  const [deliveryCity, setDeliveryCity] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [wantsSameDay, setWantsSameDay] = useState(true);
  const [sameDayFee, setSameDayFee] = useState(0);
  const [deliveryNote, setDeliveryNote] = useState("");
  const [giftMessage, setGiftMessage] = useState("");
  const [selectedSavedAddress, setSelectedSavedAddress] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [cartInitialized, setCartInitialized] = useState(false);
  const [validHamper, setValidHamper] = useState(false);
  const hamperIdRef = useRef(null);
  const saveTimerRef = useRef(null);

  const [alert, setAlert] = useState({
    open: false,
    message: "",
    type: "info",
  });

  const allSelectedAccessories = useMemo(() => {
    return [...selectedAccessories];
  }, [selectedAccessories]);

  const allSelectedVips = useMemo(() => {
    return [...selectedVips];
  }, [selectedVips]);
  

  const clearAccessories = () => setSelectedAccessories([]);
  const clearVips = () => setSelectedVips([]);

  // --- add helper to dedupe by _id ---
  const dedupeById = (arr = []) => {
    const map = new Map();
    for (const it of arr) {
      if (it && it._id != null) map.set(String(it._id), it);
    }
    return Array.from(map.values());
  };

  // --- add this: disband hamper into normal items ---
  const exitBuildModeAndFlatten = useCallback((
    { flowers = [], accessories = [], vips = [] } = {}
  ) => {
    // turn off build mode & clear hamper identity
    buildModeRef.current = false;
    buildModeUsedRef.current = false;
    hamperIdRef.current = null;

    // remove hamper state
    setHamperType("");          // important → validHamper becomes false
    setHamperItems([]);         // nothing in hamper anymore

    // merge any passed-in items and clear inHamper flags
    setSelectedFlowers(prev =>
      dedupeById([...prev, ...flowers]).map(i => ({ ...i, inHamper: false }))
    );
    setSelectedAccessories(prev =>
      dedupeById([...prev, ...accessories]).map(i => ({ ...i, inHamper: false }))
    );
    setSelectedVips(prev =>
      dedupeById([...prev, ...vips]).map(i => ({ ...i, inHamper: false }))
    );
  }, []);


  // Determine if there’s a completed hamper in build mode
  useEffect(() => {
    setValidHamper(
      buildModeUsedRef.current &&
      ["BlossomDelight", "CharmingBloom", "VIPOpulence"].includes(hamperType)
    );
  }, [hamperType]);
    
  useEffect(() => {
    if (validHamper) {
      if (!hamperIdRef.current) {
        hamperIdRef.current = nanoid();
      }
    } else {
      // clear when there's no hamper
      hamperIdRef.current = null;
    }
  }, [validHamper]);

  const computedHamperItems = useMemo(() => {
    if (!validHamper) return [];

    // use stable id (effect should have set it) — fallback to a new one but also store it
    const hid = hamperIdRef.current || nanoid();
    if (!hamperIdRef.current) hamperIdRef.current = hid;

    const items = [];
    const flower = selectedFlowers[0];
    if (flower) items.push({ ...flower, inHamper: true, hamperId: hid, type: "flower" });

    if (hamperType === "BlossomDelight") {
      selectedAccessories.slice(0, 1).forEach(a => items.push({ ...a, inHamper: true, hamperId: hid, type: "accessory" }));
    } else if (hamperType === "CharmingBloom") {
      selectedAccessories.slice(0, 2).forEach(a => items.push({ ...a, inHamper: true, hamperId: hid, type: "accessory" }));
    } else if (hamperType === "VIPOpulence") {
      selectedVips.slice(0, 5).forEach(v => items.push({ ...v, inHamper: true, hamperId: hid, type: "vip" }));
    }

    return items;
  }, [validHamper, hamperType, selectedFlowers, selectedAccessories, selectedVips]);


  useEffect(() => {
    const next = computedHamperItems || [];

    // cheap equality check by length + ids to avoid frequent sets
    const sameLength = Array.isArray(hamperItems) && hamperItems.length === next.length;
    if (sameLength) {
      let equal = true;
      for (let i = 0; i < next.length; i++) {
        if (String(hamperItems[i]?._id) !== String(next[i]?._id)) {
          equal = false;
          break;
        }
      }
      if (equal) {
        // nothing changed
        return;
      }
    }

    // set the canonical hamperItems (array)
    setHamperItems(next);

    // helper to mark items with inHamper and hamperId (or remove them)
    const markWithHamper = (list) => {
      return (list || []).map(item => {
        const match = next.find(h => String(h._id) === String(item._id));
        if (match) {
          return { ...item, inHamper: true, hamperId: match.hamperId };
        }
        // ensure we don't keep a stale hamperId
        return { ...item, inHamper: false, hamperId: null };
      });
    };

    setSelectedFlowers(prev => {
      const updated = markWithHamper(prev);
      if (JSON.stringify(prev) === JSON.stringify(updated)) return prev;
      return updated;
    });

    setSelectedAccessories(prev => {
      const updated = markWithHamper(prev);
      if (JSON.stringify(prev) === JSON.stringify(updated)) return prev;
      return updated;
    });

    setSelectedVips(prev => {
      const updated = markWithHamper(prev);
      if (JSON.stringify(prev) === JSON.stringify(updated)) return prev;
      return updated;
    });

  }, [computedHamperItems, hamperItems]); // intentionally: depend on computedHamperItems


  const extras = useMemo(() => {
    if (!validHamper) {
      return {
        flowers: [...selectedFlowers],
        accessories: [...selectedAccessories],
        vips: [...selectedVips],
      };
    }
    return {
      flowers: selectedFlowers.filter(f => !f.inHamper),
      accessories: selectedAccessories.filter(a => !a.inHamper),
      vips: selectedVips.filter(v => !v.inHamper),
    };
  }, [validHamper, selectedFlowers, selectedAccessories, selectedVips]);


  const safePrice = (p) => {
    const num = parseFloat(p);
    return isNaN(num) ? 0 : num;
  };

  const hamperSubtotal = hamperItems && hamperItems.length > 0
    ? hamperItems.reduce(
        (sum, item) => sum + parseFloat(item?.price || 0),
        0
      )
    : 0;

  // ✅ Calculate extras total
  const extrasTotal =
    extras.flowers.reduce((sum, f) => sum + safePrice(f?.price) * (f.quantity || 1), 0) +
    extras.accessories.reduce((sum, a) => sum + safePrice(a?.price) * (a.quantity || 1), 0) +
    extras.vips.reduce((sum, v) => sum + safePrice(v?.price) * (v.quantity || 1), 0);

  // ✅ Base hamper calculations
  const hamperSubtotalTotal = hamperSubtotal * hamperQuantity;
  const hamperDiscountRate = HAMPER_DISCOUNT[hamperType] || 0;
  const hamperDiscount = hamperSubtotalTotal * hamperDiscountRate;

  // ✅ Subtotal before tax
  const hamperTotal = hamperSubtotalTotal - hamperDiscount;
  const subtotal = hamperTotal + extrasTotal;

  // ✅ Tax calculation (6.25%)
  const TAX_RATE = 0.0625;
  const taxAmount = Number((subtotal * TAX_RATE).toFixed(2));

  // ✅ Final total including same-day fee and tax
  const totalAmount = subtotal + taxAmount + sameDayFee; // promo discount handled separately


  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/settings`);
        const data = await res.json();
        setSiteSettings(data);
      } catch (err) {
        console.error("Failed to fetch site settings", err);
      }
    };

    fetchSettings();
  }, [BASE_URL]);


  const currentHamperType = (
    flowers = selectedFlowers,
    accessories = selectedAccessories,
    vips = selectedVips
  ) => {
    // ✅ VIP hampers take highest priority
    if (flowers.length === 1 && vips.length > 0 && vips.length <= 5) {
      return "VIPOpulence";
    }

    // ✅ Non-VIP hampers
    if (flowers.length === 1 && accessories.length === 1) {
      return "BlossomDelight";
    }

    if (flowers.length === 1 && accessories.length === 2) {
      return "CharmingBloom";
    }

    return null;
  };

  const clearAllSelections = () => {
    setSelectedAccessories([]);
    setSelectedFlowers([]);
    setSelectedVips([]);
    setHamperItems([]);
    setHamperQuantity(1);
  };

  const resetCartState = useCallback(() => {
    setHamperItems([]);
    setSelectedAccessories([]);
    setSelectedFlowers([]);
    setSelectedVips([]);
    setHamperType("");
    buildModeRef.current = false;
    buildModeUsedRef.current = false;
    setUseSavedAddress(false);
    setDeliveryCity("");
    setDeliveryAddress("");
    setWantsSameDay(true);
    setSameDayFee(0);
    setHamperQuantity(1);
    setDeliveryNote("");
    setGiftMessage("");
    setSelectedSavedAddress(null);
    setPaymentMethod("");
  }, []);

  const isAfterCutoff = useCallback(() => {
    if (!siteSettings?.cutoffTime) return false;

    const [cutoffHour, cutoffMinute] = siteSettings.cutoffTime.split(":").map(Number);

    const now = new Date();
    const cutoff = new Date();

    cutoff.setHours(cutoffHour);
    cutoff.setMinutes(cutoffMinute);
    cutoff.setSeconds(0);
    cutoff.setMilliseconds(0);

    return now.getTime() > cutoff.getTime();
  }, [siteSettings]);

  const syncDeliveryFee = useCallback(() => {
    try {
      if (!siteSettings?.sameDayFee) {
        setSameDayFee(0);
        return;
      }

      if (wantsSameDay && isAfterCutoff()) {
        setSameDayFee(Number(siteSettings.sameDayFee) || 0);
      } else {
        setSameDayFee(0);
      }
    } catch (err) {
      console.error("Failed to compute same day delivery fee:", err);
      setSameDayFee(0);
    }
  }, [wantsSameDay, siteSettings, isAfterCutoff]);

  const handleSetSelectedSavedAddress = async (address) => {
    setSelectedSavedAddress(address);
    setDeliveryAddress(address?.address || "");
    setDeliveryCity(address?.city || "");
    setUseSavedAddress(true);

    // Optional: comment out this if backend auto-saves selected address elsewhere
    try {
      await axios.post(`${BASE_URL}/api/addresses`, { address });
    } catch (error) {
      console.error("Failed to save selected address:", error);
    }
  };

  const saveCartToBackend = useCallback(async () => {
    if (!userEmail) return;

    try {
      const payloadHamperItems = validHamper ? computedHamperItems : hamperItems || [];

      const payload = {
        selectedAccessories,
        selectedFlowers,
        selectedVips,
        hamperType,
        hamperQuantity,
        hamperItems: payloadHamperItems,
        useSavedAddress,
        deliveryCity,
        deliveryAddress,
        wantsSameDay,
        deliveryNote,
        giftMessage,
        selectedSavedAddress,
        buildModeUsed: buildModeUsedRef.current,
      };

      // console.log("🛒 Saving payload:", JSON.stringify(payload, null, 2));

      // Fire-and-forget but still await so we don't spawn overlapping requests from the same trigger.
      await axios.post(`${BASE_URL}/api/cart`, payload, {
        withCredentials: true,
        timeout: 15000, // sensible timeout
      });

      // do NOT set local cart state based on server response here — prevents feedback loop
    } catch (error) {
      console.error("❌ Failed to save cart:", error);
    }
  }, [
    userEmail,
    selectedAccessories,
    selectedVips,
    selectedFlowers,
    hamperType,
    hamperQuantity,
    useSavedAddress,
    deliveryCity,
    deliveryAddress,
    wantsSameDay,
    deliveryNote,
    giftMessage,
    selectedSavedAddress,
    BASE_URL,
    computedHamperItems,
    validHamper,
    hamperItems,
  ]);


  useEffect(() => {
  if (!userEmail || !cartInitialized) return;

  // Clear previous pending save
  if (saveTimerRef.current) {
    clearTimeout(saveTimerRef.current);
  }

  // Debounce — 700ms after last change we call save
    saveTimerRef.current = setTimeout(() => {
      saveCartToBackend();
      saveTimerRef.current = null;
    }, 700);

    // Cleanup
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
    // Keep dependencies representative of the cart data that should trigger a save.
  }, [
    userEmail,
    selectedAccessories,
    selectedVips,
    selectedFlowers,
    hamperType,
    hamperQuantity,
    computedHamperItems,
    useSavedAddress,
    deliveryCity,
    deliveryAddress,
    wantsSameDay,
    deliveryNote,
    giftMessage,
    selectedSavedAddress,
    cartInitialized,
    saveCartToBackend,
  ]);

  const loadCartFromBackend = useCallback(async () => {
    if (!userEmail) return;

    try {
      const { data } = await axios.get(`${BASE_URL}/api/cart`, {
        withCredentials: true,
      });

      const {
        selectedAccessories = [],
        selectedFlowers = [],
        selectedVips = [],
        hamperType = "",
        hamperQuantity: rawHamperQuantity = 1,
        hamperItems = [],
        useSavedAddress = false,
        deliveryCity = "",
        deliveryAddress = "",
        wantsSameDay = true,
        deliveryNote = "",
        giftMessage = "",
        selectedSavedAddress = null,
        buildModeUsed = false,
      } = data || {};

      const hamperQuantity = rawHamperQuantity > 0 ? rawHamperQuantity : 1;

      // 🟢 Gather all product IDs from cart
      const allProductIds = [
        ...selectedFlowers.map(f => f.productId),
        ...selectedAccessories.map(a => a.productId),
        ...selectedVips.map(v => v.productId),
      ];

      // 🟢 Fetch latest stock data from products
      const { data: stockData } = await axios.post(`${BASE_URL}/api/products/stock`, {
        productIds: allProductIds,
      });

      // 🟢 Helper to merge stockQty back into items
      const mergeStock = (items) =>
        items.map((item) => {
          const match = stockData.find(p => p._id === item.productId);
          return { ...item, stockQty: match ? match.stockQty : 0 };
        });

      // 🟢 Update state with fresh stock quantities
      setSelectedFlowers(mergeStock(selectedFlowers));
      setSelectedAccessories(mergeStock(selectedAccessories));
      setSelectedVips(mergeStock(selectedVips));
      setHamperType(hamperType);
      setHamperQuantity(hamperQuantity);
      setHamperItems(hamperItems);
      setUseSavedAddress(useSavedAddress);
      setDeliveryCity(deliveryCity);
      setDeliveryAddress(deliveryAddress);
      setWantsSameDay(wantsSameDay);
      setDeliveryNote(deliveryNote);
      setGiftMessage(giftMessage);
      setSelectedSavedAddress(selectedSavedAddress);

      // ✅ Sync refs
      buildModeRef.current = buildModeUsed;
      buildModeUsedRef.current = buildModeUsed;

      setCartInitialized(true);
    } catch (error) {
      console.error("❌ Failed to load cart:", error);
    }
  }, [userEmail, BASE_URL]);


  useEffect(() => {
    loadCartFromBackend();
  }, [loadCartFromBackend, userEmail]);

  const removeHamperById = useCallback((hamperId) => {
    if (!hamperId) return;

    // remove all items that belong to this hamper
    setSelectedFlowers(prev => prev.filter(f => String(f.hamperId) !== String(hamperId)));
    setSelectedAccessories(prev => prev.filter(a => String(a.hamperId) !== String(hamperId)));
    setSelectedVips(prev => prev.filter(v => String(v.hamperId) !== String(hamperId)));

    // clear hamper state
    setHamperItems([]);
    setHamperType("");
    setHamperQuantity(1);

    // reset build mode flags & id
    buildModeRef.current = false;
    buildModeUsedRef.current = false;
    hamperIdRef.current = null;
  }, []);


  // ✅ Add Accessory
  const addAccessory = (accessory) => {
    setSelectedAccessories((prev) => {
      const exists = prev.find((a) => a._id === accessory._id);
      const updated = exists
        ? prev.map((a) =>
            a._id === accessory._id
              ? { ...a, quantity: (a.quantity || 1) + 1 }
              : a
          )
        : [...prev, { ...accessory, quantity: 1 }];

      if (buildModeRef.current) {
        // ✅ Pass correct params: flowers, accessories, vips
        const type = currentHamperType(selectedFlowers, updated, selectedVips);
        setHamperType(type);
      }

      return updated;
    });
  };

  // ✅ Remove Accessory
  const removeAccessory = (_id) => {
    setSelectedAccessories((prev) => {
      const updated = prev.filter((a) => a._id !== _id);

      if (buildModeRef.current) {
        // ✅ Pass correct params
        const type = currentHamperType(selectedFlowers, updated, selectedVips);
        setHamperType(type);
      }

      return updated;
    });
  };

  // ✅ Add VIP
  const addVip = (vip) => {
    setSelectedVips((prev) => {
      const exists = prev.find((a) => a._id === vip._id);
      const updated = exists
        ? prev.map((a) =>
            a._id === vip._id
              ? { ...a, quantity: (a.quantity || 1) + 1 }
              : a
          )
        : [...prev, { ...vip, quantity: 1 }];

      if (buildModeRef.current) {
        // ✅ Pass correct params: flowers, accessories, vips
        const type = currentHamperType(selectedFlowers, selectedAccessories, updated);
        setHamperType(type);
      }

      return updated;
    });
  };

  // ✅ Remove VIP
  const removeVip = (_id) => {
    setSelectedVips((prev) => {
      const updated = prev.filter((a) => a._id !== _id);

      if (buildModeRef.current) {
        // ✅ Pass correct params
        const type = currentHamperType(selectedFlowers, selectedAccessories, updated);
        setHamperType(type);
      }

      return updated;
    });
  };

  const addFlower = (flower) => {
    setSelectedFlowers((prev) => {
      const exists = prev.find((f) => f._id === flower._id);
      const updated = exists
        ? prev.map((f) =>
            f._id === flower._id
              ? { ...f, quantity: (f.quantity || 1) + 1 }
              : f
          )
        : [...prev, { ...flower, quantity: 1 }];

      if (buildModeRef.current) {
        // ✅ Pass correct params
        const type = currentHamperType(updated, selectedAccessories, selectedVips);
        setHamperType(type);
      }

      return updated;
    });
  };

  const removeFlower = (_id) => {
    setSelectedFlowers((prev) => {
      const updated = prev.filter((f) => f._id !== _id);

      if (buildModeRef.current) {
        // ✅ Pass correct params
        const type = currentHamperType(updated, selectedAccessories, selectedVips);
        setHamperType(type);
      }

      return updated;
    });
  };

  const setFlower = (flower) => {
    setSelectedFlowers([flower]);
    buildModeRef.current = true;
    buildModeUsedRef.current = true;

    // ✅ Always prioritize VIP hampers if any VIPs are selected (up to 5 allowed)
    if (selectedVips.length > 0 && selectedVips.length <= 5) {
      setHamperType("VIPOpulence");
    } else {
      const type = currentHamperType([flower], selectedAccessories, selectedVips);
      setHamperType(type);
    }
  };

  const clearFlower = () => {
    setSelectedFlowers([]);
    buildModeRef.current =
      selectedAccessories.length > 0 || selectedVips.length > 0;

    // ✅ Dynamically recalculate hamper type instead of hardcoding VIPOpulence
    const type = currentHamperType([], selectedAccessories, selectedVips);
    setHamperType(type);
  };

  const updateHamperQuantity = (delta) => {
    setHamperQuantity((prevQuantity) => {
      const newQuantity = Math.max(1, prevQuantity + delta);

      // If reducing hamper quantity, we don't need checks
      if (delta < 0) return newQuantity;

      // ✅ Check stock for flowers in hamper
      for (const flower of selectedFlowers.slice(0, hamperFlowerSlots)) {
        if (flower.stockQty && flower.stockQty < newQuantity) {
          setAlert({
            open: true,
            message: `Only ${flower.stockQty} units available for "${flower.name}"`,
            type: "warning",
          });
          return prevQuantity; // Prevent exceeding stock
        }
      }

      // ✅ Check stock for accessories in hamper
      for (const accessory of selectedAccessories.slice(0, hamperAccessorySlots)) {
        if (accessory.stockQty && accessory.stockQty < newQuantity) {
          setAlert({
            open: true,
            message: `Only ${accessory.stockQty} units available for "${accessory.name}"`,
            type: "warning",
          });
          return prevQuantity;
        }
      }

      // ✅ Check stock for VIPs in hamper
      for (const vip of selectedVips.slice(0, hamperVipSlots)) {
        if (vip.stockQty && vip.stockQty < newQuantity) {
          setAlert({
            open: true,
            message: `Only ${vip.stockQty} units available for "${vip.name}"`,
            type: "warning",
          });
          return prevQuantity;
        }
      }

      // ✅ If all checks pass, allow quantity change
      return newQuantity;
    });
  };


  const updateFlowerQuantity = (_id, delta) => {
    setSelectedFlowers((prev) =>
      prev.map((f) => {
        if (f._id === _id) {
          const newQty = Math.max(1, (f.quantity || 1) + delta);

          // ✅ Check stock availability
          if (f.stockQty && newQty > f.stockQty) {
            setAlert({
              open: true,
              message: `Only ${f.stockQty} units available for "${f.name}"`,
              type: "warning",
            });
            return f; // Prevent exceeding stock
          }

          return {
            ...f,
            quantity: newQty,
          };
        }
        return f;
      })
    );
  };

  const updateAccessoryQuantity = (_id, delta) => {
    setSelectedAccessories((prev) =>
      prev.map((a) => {
        if (a._id === _id) {
          const newQty = Math.max(1, (a.quantity || 1) + delta);

          // ✅ Check stock availability
          if (a.stockQty && newQty > a.stockQty) {
            setAlert({
              open: true,
              message: `Only ${a.stockQty} units available for "${a.name}"`,
              type: "warning",
            });
            return a; // Prevent exceeding stock
          }

          return {
            ...a,
            quantity: newQty,
          };
        }
        return a;
      })
    );
  };

  const updateVipQuantity = (_id, delta) => {
    setSelectedVips((prev) =>
      prev.map((v) => {
        if (v._id === _id) {
          const newQty = Math.max(1, (v.quantity || 1) + delta);

          // ✅ Check stock availability
          if (v.stockQty && newQty > v.stockQty) {
            setAlert({
              open: true,
              message: `Only ${v.stockQty} units available for "${v.name}"`,
              type: "warning",
            });
            return v; // Prevent exceeding stock
          }

          return {
            ...v,
            quantity: newQty,
          };
        }
        return v;
      })
    );
  };


  const clearCart = async () => {
    setSelectedAccessories([]);
    setSelectedFlowers([]);
    setSelectedVips([]);
    setHamperItems([]);
    setHamperType(null);
    buildModeRef.current = false;
    buildModeUsedRef.current = false;
    setUseSavedAddress(false);
    setDeliveryCity("");
    setDeliveryAddress("");
    setWantsSameDay(true);
    setSameDayFee(0);
    setHamperQuantity(1);
    setDeliveryNote("");
    setGiftMessage("");
    setSelectedSavedAddress(null);

    // Save the cleared cart to backend
    try {
      if (userEmail) {
        await fetch(`${BASE_URL}/api/cart`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include", // ✅ Still needed to send cookies
          body: JSON.stringify({
            selectedAccessories: [],
            selectedFlowers: [],
            selectedVips: [],
            hamperType: "",
            hamperQuantity: 1,
            hamperItems: [],
            useSavedAddress: false,
            deliveryCity: "",
            deliveryAddress: "",
            wantsSameDay: true,
            deliveryNote: "",
            giftMessage: "",
            selectedSavedAddress: null,
          }),
        });
      }
    } catch (err) {
      console.error("Failed to clear cart on backend:", err);
    }
  };


  const hamperUnits =
  buildModeUsedRef.current && hamperType
    ? hamperQuantity > 0 ? hamperQuantity : 1
    : 0;

  const hamperFlowerSlots = hamperType ? 1 : 0;

  const hamperAccessorySlots =
    hamperType === "BlossomDelight" ? 1 :
    hamperType === "CharmingBloom" ? 2 : 0;

  const hamperVipSlots = hamperType === "VIPOpulence" ? 5 : 0;

  // Extras beyond the hamper slots
  const extraFlowerUnits = Math.max(0, selectedFlowers.length - hamperFlowerSlots);

  const extraAccessoryUnits = Math.max(0, selectedAccessories.length - hamperAccessorySlots);

  const extraVipUnits = Math.max(0, selectedVips.length - hamperVipSlots);

  const extrasUnits = extraFlowerUnits + extraAccessoryUnits + extraVipUnits;

  // Total cart count
  const cartCount = hamperUnits + extrasUnits;

  return (
    <CartContext.Provider
      value={{
        selectedAccessories,
        selectedFlowers,
        selectedFlower,
        setSelectedFlower,
        setSelectedFlowers,
        selectedVips,
        setSelectedAccessories,
        setSelectedVips,
        allSelectedAccessories,
        allSelectedVips,
        removeHamperById,
        addAccessory,
        removeAccessory,
        addVip,
        removeVip,
        addFlower,
        removeFlower,
        setFlower,
        clearFlower,
        clearAccessories,
        clearVips,
        updateFlowerQuantity,
        updateAccessoryQuantity,
        updateVipQuantity,
        clearCart,
        cartCount,
        isAfterCutoff,
        alert,
        setAlert,

        hamperType,
        setHamperType,
        hamperQuantity,
        setHamperQuantity,
        updateHamperQuantity,
        hamperItems,
        setHamperItems,
        extras,
        currentHamperType,
        buildModeRef,
        buildModeUsedRef,
        taxAmount,
        TAX_RATE,
        totalAmount,
        subtotal,
        hamperDiscount,
        computedHamperItems,
        validHamper,
        exitBuildModeAndFlatten,
        clearAllSelections,

        siteSettings,
        setSiteSettings,
        useSavedAddress,
        setUseSavedAddress,
        deliveryCity,
        setDeliveryCity,
        deliveryAddress,
        setDeliveryAddress,
        wantsSameDay,
        setWantsSameDay,
        sameDayFee,
        setSameDayFee,
        syncDeliveryFee,
        deliveryNote,
        setDeliveryNote,
        giftMessage,
        setGiftMessage,
        selectedSavedAddress,
        setSelectedSavedAddress: handleSetSelectedSavedAddress,
        paymentMethod,
        setPaymentMethod,

        resetCartState,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;

